import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from '../cx';
import { FieldShell } from '../internal/FieldShell';
import styles from './Input.module.css';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  leftSlot?: ReactNode;
  rightSlot?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, optional, leftSlot, rightSlot, id, className, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <FieldShell
      htmlFor={inputId}
      hintId={hintId}
      errorId={errorId}
      {...(label !== undefined ? { label } : {})}
      {...(hint !== undefined ? { hint } : {})}
      {...(error !== undefined ? { error } : {})}
      {...(optional !== undefined ? { optional } : {})}
    >
      <div className={styles.wrapper}>
        {leftSlot && <span className={styles.leftSlot}>{leftSlot}</span>}
        <input
          ref={ref}
          id={inputId}
          className={cx(
            styles.input,
            error && styles.invalid,
            Boolean(leftSlot) && styles.hasLeftSlot,
            Boolean(rightSlot) && styles.hasRightSlot,
            className,
          )}
          aria-invalid={Boolean(error) || undefined}
          {...(describedBy ? { 'aria-describedby': describedBy } : {})}
          {...rest}
        />
        {rightSlot && <span className={styles.rightSlot}>{rightSlot}</span>}
      </div>
    </FieldShell>
  );
});
